import { Controller } from "@hotwired/stimulus"

/**
 * Hero Shot Studio: composes a screenshot into a marketing hero image on a
 * canvas, entirely in the browser.
 *
 * The screenshot never leaves the page — it is read from a file, the clipboard
 * or a drop, drawn onto a canvas with the brand treatment (rounded card,
 * hairline border, soft shadow, optional bottom fade, a background gradient,
 * a logo watermark and lockup), and exported with toBlob. Every control on the
 * page carries `data-setting`; `update` reads the name and the value type off
 * the element, so a new option is one input and one key in `Settings`.
 *
 * The watermark is painted on its own layer and erased under the card's full
 * footprint before the card is drawn. Without that, a faded card lets the mark
 * show through its lower half, which reads as a rendering mistake.
 */

type Layout = "fade" | "bleed" | "focus" | "float"
type Watermark = "none" | "gray" | "color" | "white"
type Corner = "top-right" | "top-left" | "bottom-right" | "bottom-left"
type Lockup = "none" | "bottom-center" | "bottom-right"

export interface Settings {
  size: string
  width: number
  height: number
  layout: Layout
  background: string
  bgTop: string
  bgBottom: string
  scale: number
  offsetX: number
  offsetY: number
  radius: number
  shadow: number
  borderWidth: number
  borderColor: string
  fade: boolean
  fadeStart: number
  fadeEnd: number
  zoom: number
  focusX: number
  focusY: number
  watermark: Watermark
  watermarkCorner: Corner
  watermarkOpacity: number
  watermarkSize: number
  lockup: Lockup
  lockupSize: number
  filename: string
  // A typed file name survives screenshot swaps; an automatic one follows them.
  filenameCustom: boolean
}

interface Glow {
  color: string
  x: number
  y: number
  radius: number
  alpha: number
}

interface Background {
  stops: string[]
  angle: number
  glows: Glow[]
  border: string
  dark: boolean
}

// Brand palette: teal, periwinkle, lime, orange. The neutrals stay for when
// the screenshot should carry the colour; the brand presets are a wash of one
// hue plus a soft radial glow, so the gradient reads as light rather than as a
// flat two-stop fill.
const TEAL = "#287379"
const PERIWINKLE = "#9a98e1"
const LIME = "#acc700"
const ORANGE = "#ff9700"

const glow = (color: string, x: number, y: number, alpha: number, radius = 0.55): Glow => ({ color, x, y, radius, alpha })

export const BACKGROUNDS: Record<string, Background> = {
  slate: { stops: ["#f8fafc", "#eef2f7"], angle: 180, glows: [], border: "#e2e8f0", dark: false },
  cream: { stops: ["#fbf9f5", "#efe9de"], angle: 180, glows: [], border: "#e7e0d4", dark: false },
  mint: { stops: ["#f2f8f7", "#dbeae7"], angle: 180, glows: [], border: "#d5e4e1", dark: false },
  sky: { stops: ["#f4f8fc", "#dbe6f1"], angle: 180, glows: [], border: "#d6e1ec", dark: false },
  lavender: { stops: ["#f7f6fc", "#e4e0f2"], angle: 180, glows: [], border: "#dcd7ee", dark: false },
  teal: { stops: ["#f1f8f8", "#d3e7e8"], angle: 165, glows: [glow(TEAL, 0.1, 0.05, 0.22)], border: "#cde0e1", dark: false },
  periwinkle: { stops: ["#f6f5fd", "#dedcf5"], angle: 195, glows: [glow(PERIWINKLE, 0.88, 0.08, 0.3)], border: "#d8d6ef", dark: false },
  lime: { stops: ["#f9fbec", "#e7efc3"], angle: 160, glows: [glow(LIME, 0.1, 0.92, 0.2)], border: "#dde6b6", dark: false },
  orange: { stops: ["#fff7ec", "#ffe5c4"], angle: 200, glows: [glow(ORANGE, 0.9, 0.88, 0.2)], border: "#f5dcba", dark: false },
  brand: {
    stops: ["#f7f9fa", "#e8eef0"],
    angle: 180,
    glows: [glow(TEAL, 0.08, 0.05, 0.22), glow(PERIWINKLE, 0.92, 0.9, 0.22), glow(ORANGE, 0.92, 0.05, 0.1, 0.4), glow(LIME, 0.08, 0.92, 0.1, 0.4)],
    border: "#dae2e5",
    dark: false,
  },
}

// What each layout changes when picked. Anything not listed is left as the
// user had it, so switching layouts does not undo a colour or watermark choice.
export const LAYOUTS: Record<Layout, Partial<Settings>> = {
  fade: { scale: 77.5, offsetX: 0, offsetY: 12, fade: true, fadeStart: 50, fadeEnd: 100, shadow: 45, zoom: 1 },
  bleed: { scale: 94, offsetX: 22, offsetY: 17, fade: false, shadow: 55, zoom: 1 },
  focus: { scale: 92, offsetX: 0, offsetY: 16, fade: true, fadeStart: 55, fadeEnd: 100, shadow: 45, zoom: 1.6 },
  float: { scale: 70, offsetX: 0, offsetY: 15, fade: false, shadow: 75, zoom: 1 },
}

export const DEFAULTS: Settings = {
  size: "1600x900",
  width: 1600,
  height: 900,
  layout: "fade",
  background: "slate",
  bgTop: BACKGROUNDS.slate.stops[0],
  bgBottom: BACKGROUNDS.slate.stops[1],
  scale: 77.5,
  offsetX: 0,
  offsetY: 12,
  radius: 20,
  shadow: 45,
  borderWidth: 2,
  borderColor: BACKGROUNDS.slate.border,
  fade: true,
  fadeStart: 50,
  fadeEnd: 100,
  zoom: 1,
  focusX: 15,
  focusY: 20,
  watermark: "gray",
  watermarkCorner: "top-right",
  watermarkOpacity: 14,
  watermarkSize: 48,
  lockup: "none",
  lockupSize: 24,
  filename: "hero-shot.png",
  filenameCustom: false,
}

const STORAGE_KEY = "hero-shot-studio.settings"
// The number inputs carry min/max for the browser's own validation, but a typed
// or pasted value arrives here unchecked; the canvas has to be bounded anyway.
const MIN_DIMENSION = 200
const MAX_DIMENSION = 4000
// The marks are 40-unit SVGs; rasterizing them at their own size and scaling
// up would blur, so they are serialized at export resolution.
const MARK_RASTER = 1024
const LOCKUP_RASTER = 1600

export default class extends Controller<HTMLElement> {
  static targets = ["canvas", "placeholder", "status", "file", "customSize", "focusFields", "mark", "lockup", "readout"]

  declare readonly canvasTarget: HTMLCanvasElement
  declare readonly placeholderTarget: HTMLElement
  declare readonly statusTarget: HTMLElement
  declare readonly fileTarget: HTMLInputElement
  declare readonly customSizeTarget: HTMLElement
  declare readonly focusFieldsTarget: HTMLElement
  declare readonly markTarget: HTMLElement
  declare readonly lockupTarget: HTMLElement
  declare readonly readoutTargets: HTMLElement[]

  settings: Settings = { ...DEFAULTS }
  image: HTMLImageElement | null = null
  private mark: HTMLImageElement | null = null
  private lockup: HTMLImageElement | null = null
  private frame: number | null = null
  // Decoding is asynchronous, so a slow earlier pick can finish after a later
  // one. Each load takes a token and only the newest is allowed to land.
  private loadToken = 0
  private pendingUrl: string | null = null
  private imageUrl: string | null = null

  async connect() {
    this.settings = { ...DEFAULTS, ...this.stored() }
    this.syncControls()
    ;[this.mark, this.lockup] = await Promise.all([
      this.rasterize(this.markTarget, MARK_RASTER),
      this.rasterize(this.lockupTarget, LOCKUP_RASTER),
    ])
    this.render()
  }

  disconnect() {
    this.releaseUrl()
    this.releaseUrl(this.imageUrl)
    if (this.frame !== null) cancelAnimationFrame(this.frame)
  }

  // --- input -------------------------------------------------------------

  pick(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0]
    if (file) this.loadFile(file)
  }

  paste(event: ClipboardEvent) {
    const item = Array.from(event.clipboardData?.items ?? []).find((i) => i.type.startsWith("image/"))
    const file = item?.getAsFile()
    if (!file) return

    event.preventDefault()
    this.loadFile(file)
  }

  dragOver(event: DragEvent) {
    event.preventDefault()
  }

  drop(event: DragEvent) {
    event.preventDefault()
    const file = event.dataTransfer?.files?.[0]
    if (file?.type.startsWith("image/")) this.loadFile(file)
  }

  loadFile(file: File) {
    this.releaseUrl()
    const token = ++this.loadToken
    const url = URL.createObjectURL(file)
    this.pendingUrl = url

    const image = new Image()
    // Screen-capture extensions assign crossOrigin from inside drawImage. A
    // change restarts the load and defers the draw; the same value does nothing.
    image.crossOrigin = "anonymous"
    image.onload = () => {
      if (token !== this.loadToken) return

      this.releaseUrl(this.imageUrl)
      this.imageUrl = url
      this.pendingUrl = null
      this.image = image
      this.placeholderTarget.classList.add("hidden")
      this.canvasTarget.classList.remove("hidden")
      if (!this.settings.filenameCustom) {
        this.settings = { ...this.settings, filename: file.name.replace(/\.[^.]+$/, "") + "-hero.png" }
      }
      this.commit()
    }
    image.onerror = () => this.releaseUrl(url)
    image.src = url
  }

  // A blob URL keeps its file alive until it is revoked. The screenshot on the
  // canvas keeps its URL until it is replaced: anything that restarts its load
  // (a crossOrigin change, see loadFile) would otherwise fetch a revoked URL
  // and leave a broken image behind.
  private releaseUrl(url: string | null = this.pendingUrl) {
    if (!url) return
    URL.revokeObjectURL(url)
    if (this.pendingUrl === url) this.pendingUrl = null
    if (this.imageUrl === url) this.imageUrl = null
  }

  // --- settings ----------------------------------------------------------

  update(event: Event) {
    const element = event.currentTarget as HTMLInputElement | HTMLSelectElement
    const key = element.dataset.setting as keyof Settings
    if (!key) return

    if (key === "width" || key === "height") {
      this.assign(key, this.clampDimension(element.value, this.settings[key]))
    } else {
      this.assign(key, this.valueOf(element))
    }
    if (key === "size") this.applySize(element.value)
    if (key === "filename") this.assign("filenameCustom", element.value.trim() !== "")
    this.commit()
  }

  applyLayout(event: Event) {
    const layout = (event.currentTarget as HTMLSelectElement).value as Layout
    this.settings = { ...this.settings, layout, ...LAYOUTS[layout] }
    this.commit()
  }

  applyBackground(event: Event) {
    const background = (event.currentTarget as HTMLSelectElement).value
    const preset = BACKGROUNDS[background]
    this.settings = preset
      ? {
          ...this.settings,
          background,
          bgTop: preset.stops[0],
          bgBottom: preset.stops[preset.stops.length - 1],
          borderColor: preset.border,
        }
      : { ...this.settings, background }
    this.commit()
  }

  updateCustomColor(event: Event) {
    const element = event.currentTarget as HTMLInputElement
    const key = element.dataset.setting as "bgTop" | "bgBottom"
    this.settings = { ...this.settings, [key]: element.value, background: "custom" }
    this.commit()
  }

  reset() {
    this.settings = { ...DEFAULTS }
    this.commit()
  }

  private assign<K extends keyof Settings>(key: K, value: Settings[K]) {
    this.settings = { ...this.settings, [key]: value }
  }

  private valueOf(element: HTMLInputElement | HTMLSelectElement): Settings[keyof Settings] {
    if (element instanceof HTMLInputElement && element.type === "checkbox") return element.checked
    if (element instanceof HTMLInputElement && (element.type === "range" || element.type === "number")) {
      return Number(element.value)
    }
    return element.value as Settings[keyof Settings]
  }

  private applySize(size: string) {
    const match = size.match(/^(\d+)x(\d+)$/)
    if (match) this.settings = { ...this.settings, width: Number(match[1]), height: Number(match[2]) }
  }

  // A blank or unparseable entry keeps the previous size rather than collapsing
  // the canvas; anything else is held to the supported range.
  private clampDimension(raw: string, previous: number): number {
    const value = Number(raw)
    if (raw.trim() === "" || !Number.isFinite(value)) return previous
    return Math.min(MAX_DIMENSION, Math.max(MIN_DIMENSION, Math.round(value)))
  }

  private commit() {
    this.persist()
    this.syncControls()
    this.render()
  }

  private stored(): Partial<Settings> {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}")
    } catch {
      return {}
    }
  }

  private persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings))
    } catch {
      // Private mode or a full store: the page still works, it just forgets.
    }
  }

  // Controls are written from settings, never the other way round, so a preset
  // or a reset shows up in every input without each one tracking the others.
  private syncControls() {
    const s = this.settings
    this.element.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-setting]").forEach((element) => {
      const key = element.dataset.setting as keyof Settings
      if (!(key in s)) return

      if (element instanceof HTMLInputElement && element.type === "checkbox") element.checked = Boolean(s[key])
      else element.value = String(s[key])
    })
    this.readoutTargets.forEach((readout) => {
      const key = readout.dataset.for as keyof Settings
      readout.textContent = `${s[key]}${readout.dataset.unit ?? ""}`
    })
    this.customSizeTarget.classList.toggle("hidden", s.size !== "custom")
    this.focusFieldsTarget.classList.toggle("hidden", s.layout !== "focus")
  }

  // --- rendering ---------------------------------------------------------

  render() {
    if (!this.image) return
    if (this.frame !== null) cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.paint()
    })
  }

  private paint() {
    const image = this.image
    if (!image) return

    const s = this.settings
    const canvas = this.canvasTarget
    canvas.width = s.width
    canvas.height = s.height
    const context = canvas.getContext("2d")
    if (!context) return

    const preset = this.background()
    this.paintBackground(context, preset)

    const card = this.cardRect(image)
    if (s.watermark !== "none" && this.mark) this.paintWatermark(context, card)
    this.paintCard(context, image, card, preset)
    if (s.lockup !== "none" && this.lockup) this.paintLockup(context)
  }

  // A custom background is the two colour inputs, top to bottom, with no
  // glow; its shadow follows how dark the bottom colour is.
  private background(): Background {
    const s = this.settings
    return (
      BACKGROUNDS[s.background] ?? {
        stops: [s.bgTop, s.bgBottom],
        angle: 180,
        glows: [],
        border: s.borderColor,
        dark: luminance(s.bgBottom) < 0.45,
      }
    )
  }

  // The linear gradient runs along a CSS-style angle (180 = top to bottom),
  // sized so both ends reach the canvas corners; each glow is a radial fade
  // from its colour to transparent, placed in fractions of the canvas.
  private paintBackground(context: CanvasRenderingContext2D, preset: Background) {
    const { width: w, height: h } = this.settings
    const rad = (preset.angle * Math.PI) / 180
    const dx = Math.sin(rad)
    const dy = -Math.cos(rad)
    const half = (Math.abs(w * dx) + Math.abs(h * dy)) / 2
    const gradient = context.createLinearGradient(w / 2 - dx * half, h / 2 - dy * half, w / 2 + dx * half, h / 2 + dy * half)
    preset.stops.forEach((color, index) => gradient.addColorStop(index / Math.max(1, preset.stops.length - 1), color))
    context.fillStyle = gradient
    context.fillRect(0, 0, w, h)

    for (const g of preset.glows) {
      const radius = g.radius * Math.max(w, h)
      const radial = context.createRadialGradient(g.x * w, g.y * h, 0, g.x * w, g.y * h, radius)
      radial.addColorStop(0, rgba(g.color, g.alpha))
      radial.addColorStop(1, rgba(g.color, 0))
      context.fillStyle = radial
      context.fillRect(0, 0, w, h)
    }
  }

  private cardRect(image: HTMLImageElement) {
    const s = this.settings
    const width = s.width * (s.scale / 100)
    const height = width * (image.naturalHeight / image.naturalWidth)
    const x = (s.width - width) / 2 + s.width * (s.offsetX / 100)
    const y = s.height * (s.offsetY / 100)
    return { x, y, width, height, radius: s.radius * (width / 1240) }
  }

  private paintWatermark(
    context: CanvasRenderingContext2D,
    card: { x: number; y: number; width: number; height: number; radius: number },
  ) {
    const s = this.settings
    const layer = this.layer(s.width, s.height)
    if (!layer) return

    // "color" is the header logo as it is; the others recolour its shape.
    const mark = this.mark as HTMLImageElement
    const artwork = s.watermark === "color" ? mark : this.tinted(mark, s.watermark === "white" ? "#ffffff" : "#64748b")
    if (!artwork) return

    const size = s.width * (s.watermarkSize / 100)
    // A tenth of the mark runs off the canvas, so it reads as cropped by the
    // frame rather than as a sticker placed inside it.
    const bleed = size * 0.1
    const x = s.watermarkCorner.endsWith("left") ? -bleed : s.width - size + bleed
    const y = s.watermarkCorner.startsWith("top") ? -bleed : s.height - size + bleed

    layer.context.globalAlpha = s.watermarkOpacity / 100
    layer.context.drawImage(artwork, x, y, size, size)
    layer.context.globalAlpha = 1
    layer.context.globalCompositeOperation = "destination-out"
    layer.context.beginPath()
    layer.context.roundRect(card.x, card.y, card.width, card.height, card.radius)
    layer.context.fill()
    context.drawImage(layer.canvas, 0, 0)
  }

  private paintCard(
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    card: { x: number; y: number; width: number; height: number; radius: number },
    preset: Background,
  ) {
    const s = this.settings
    const layer = this.layer(Math.ceil(card.width), Math.ceil(card.height))
    if (!layer) return

    const c = layer.context
    c.beginPath()
    c.roundRect(0, 0, card.width, card.height, card.radius)
    c.clip()

    const source = this.sourceRect(image)
    c.drawImage(image, source.x, source.y, source.width, source.height, 0, 0, card.width, card.height)

    if (s.borderWidth > 0) {
      c.strokeStyle = s.borderColor
      c.lineWidth = s.borderWidth * (card.width / 1240)
      c.beginPath()
      c.roundRect(c.lineWidth / 2, c.lineWidth / 2, card.width - c.lineWidth, card.height - c.lineWidth, card.radius)
      c.stroke()
    }

    if (s.fade && s.fadeEnd > s.fadeStart) {
      const mask = c.createLinearGradient(0, card.height * (s.fadeStart / 100), 0, card.height * (s.fadeEnd / 100))
      mask.addColorStop(0, "rgba(0,0,0,0)")
      mask.addColorStop(1, "rgba(0,0,0,1)")
      c.globalCompositeOperation = "destination-out"
      c.fillStyle = mask
      c.fillRect(0, 0, card.width, card.height)
    }

    const strength = s.shadow / 100
    const unit = s.width / 1600
    context.save()
    context.shadowColor = preset.dark ? `rgba(0,0,0,${0.7 * strength})` : `rgba(15,23,42,${0.45 * strength})`
    context.shadowBlur = 90 * strength * unit
    context.shadowOffsetY = 28 * strength * unit
    context.drawImage(layer.canvas, card.x, card.y)
    context.restore()
  }

  // The focus layout shows a zoomed window of the screenshot; every other
  // layout shows all of it.
  private sourceRect(image: HTMLImageElement) {
    const s = this.settings
    const zoom = s.layout === "focus" ? Math.max(1, s.zoom) : 1
    const width = image.naturalWidth / zoom
    const height = image.naturalHeight / zoom
    return {
      x: (image.naturalWidth - width) * (s.focusX / 100),
      y: (image.naturalHeight - height) * (s.focusY / 100),
      width,
      height,
    }
  }

  private paintLockup(context: CanvasRenderingContext2D) {
    const s = this.settings
    const lockup = this.lockup as HTMLImageElement
    const width = s.width * (s.lockupSize / 100)
    const height = width * (lockup.naturalHeight / lockup.naturalWidth)
    const margin = s.height * 0.06
    const x = s.lockup === "bottom-right" ? s.width - width - margin : (s.width - width) / 2
    context.drawImage(lockup, x, s.height - height - margin, width, height)
  }

  private tinted(mark: HTMLImageElement, color: string) {
    const layer = this.layer(MARK_RASTER, MARK_RASTER)
    if (!layer) return null

    layer.context.drawImage(mark, 0, 0, MARK_RASTER, MARK_RASTER)
    layer.context.globalCompositeOperation = "source-in"
    layer.context.fillStyle = color
    layer.context.fillRect(0, 0, MARK_RASTER, MARK_RASTER)
    return layer.canvas
  }

  private layer(width: number, height: number) {
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext("2d")
    return context ? { canvas, context } : null
  }

  // Serialized with explicit pixel dimensions so the browser rasterizes the
  // vector at export size. A data URL keeps the canvas untainted.
  private rasterize(holder: HTMLElement, width: number): Promise<HTMLImageElement | null> {
    const svg = holder.querySelector("svg")
    if (!svg) return Promise.resolve(null)

    const clone = svg.cloneNode(true) as SVGElement
    const viewBox = (clone.getAttribute("viewBox") ?? "0 0 1 1").split(/\s+/).map(Number)
    const aspect = viewBox[3] && viewBox[2] ? viewBox[3] / viewBox[2] : 1
    clone.setAttribute("width", String(width))
    clone.setAttribute("height", String(Math.round(width * aspect)))

    return new Promise((resolve) => {
      const image = new Image()
      image.crossOrigin = "anonymous"
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = `data:image/svg+xml;base64,${b64(new XMLSerializer().serializeToString(clone))}`
    })
  }

  // --- output ------------------------------------------------------------

  download() {
    if (!this.image) return
    this.paint()
    this.canvasTarget.toBlob((blob) => {
      if (!blob) return
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = this.settings.filename || DEFAULTS.filename
      link.click()
      URL.revokeObjectURL(link.href)
    }, "image/png")
  }

  async copy() {
    if (!this.image) return
    this.paint()
    const blob = await new Promise<Blob | null>((resolve) => this.canvasTarget.toBlob(resolve, "image/png"))
    try {
      if (!blob || typeof ClipboardItem === "undefined") throw new Error("clipboard unavailable")
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      this.flash(this.statusTarget.dataset.copied ?? "Copied")
    } catch {
      this.flash(this.statusTarget.dataset.copyFailed ?? "Copy failed. Download instead.")
    }
  }

  private flash(message: string) {
    this.statusTarget.textContent = message
    window.setTimeout(() => {
      if (this.statusTarget.textContent === message) this.statusTarget.textContent = ""
    }, 2500)
  }
}

// btoa only takes latin-1; an SVG can carry anything.
function b64(markup: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(markup)))
}

function channels(hex: string): [number, number, number] {
  const value = hex.replace("#", "")
  const full = value.length === 3 ? value.replace(/./g, (c) => c + c) : value
  const n = parseInt(full, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = channels(hex)
  return `rgba(${r},${g},${b},${alpha})`
}

// Relative luminance, enough to tell a dark custom background from a light one.
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => c / 255)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}
